import * as React from 'react';
import { Box, Radio, Typography } from '@mui/material';
import { useAppTranslation } from '../../../services/i18n';
import { CARD_BORDER } from '../colors';

type Props = {
  title: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
};

/**
 * One of the two large source options on Zadání kurzu.
 *
 * A card rather than a radio row, as the design has it — but it is still a
 * radio underneath: the whole card is the label, so the click target is the
 * card and a keyboard user still meets one radio group.
 */
export function ChoiceCard({ title, body, selected, onSelect }: Props) {
  const t = useAppTranslation();

  return (
    <Box
      component="label"
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        display: 'block',
        cursor: 'pointer',
        padding: '16px 18px',
        borderRadius: '8px',
        // The selected card carries a 2px frame; the unselected one a hairline.
        // Both are drawn inside the box so the row does not shift on select.
        border: selected
          ? '2px solid var(--color-primary)'
          : `1px solid ${CARD_BORDER}`,
        backgroundColor: selected ? 'var(--color-primary15)' : 'var(--color-white)',
        // Keeps the two cards the same height when one gains the thicker frame.
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{ fontWeight: 500, fontSize: '15px', color: 'var(--color-black)' }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: 'var(--color-black-text)',
          marginTop: '10px',
        }}
      >
        {body}
      </Typography>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}
      >
        <Radio
          checked={selected}
          onChange={onSelect}
          size="small"
          sx={{ padding: 0 }}
        />
        {selected && (
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '13px',
              // The darker primary, so the word stays readable on the tint and
              // still moves with a re-coloured space.
              color: 'var(--color-primary60)',
            }}
          >
            {t('klara.brief.selected')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
